import { useQuery } from '@apollo/client';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Snackbar,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import type React from 'react';
import { type ChangeEvent, type DragEvent, type KeyboardEvent, useCallback, useMemo, useRef, useState } from 'react';
import allMediagroups from 'src/api/permission/allMediaGroups';
import handleFileUpload from '../FileUploader/handleFileUpload';
import QueuedUploaderItem from './QueuedUploaderItem';

interface FormatData {
  id: string;
  fmtType?: { name: string };
  format_type?: string;
  mediapath: string;
  filename: string;
  mimetype: string;
}

interface FinalizeData {
  asset_id: string;
  asset: {
    name: string;
    media_group?: string;
    formats?: FormatData[];
  };
}

interface QueuedItem {
  id: string;
  file: File;
  fileName: string;
  title: string;
  description: string;
  progress: number;
  status: 'queued' | 'uploading' | 'done';
  error: string | null;
  isFinalizing: boolean;
  finalizeData: FinalizeData | null;
}

interface SnackState {
  open: boolean;
  severity: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

interface MediaGroupNode {
  id: string;
  name: string;
}

interface MediaGroupEdge {
  node: MediaGroupNode;
}

interface AllMediaGroupsData {
  allMediaGroups: {
    edges: MediaGroupEdge[];
  };
}

interface MetaChanges {
  title?: string;
  description?: string;
}

const QueuedUploaderManager: React.FC = () => {
  const [items, setItems] = useState<QueuedItem[]>([]);
  const [snack, setSnack] = useState<SnackState>({ open: false, severity: 'success', message: '' });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [mediaGroupName, setMediaGroupName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: mgData, loading: mgLoading, error: mgError } = useQuery<AllMediaGroupsData>(allMediagroups);

  const addFiles = useCallback((fileList: FileList): void => {
    const now = Date.now();
    const newItems: QueuedItem[] = Array.from(fileList).map((file, idx) => ({
      id: `${now}-${idx}-${file.size}`,
      file,
      fileName: file.name,
      title: file.name.replace(/\.[^.]+$/, ''),
      description: '',
      progress: 0,
      status: 'queued' as const,
      error: null,
      isFinalizing: false,
      finalizeData: null,
    }));
    setItems((prev) => [...prev, ...newItems]);
  }, []);

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const browse = (): void => inputRef.current?.click();

  const onFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files?.length) addFiles(e.target.files);
  };

  const onChangeMeta = (id: string, changes: MetaChanges): void => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  };

  const onRemove = (id: string): void => setItems((prev) => prev.filter((it) => it.id !== id));

  const uploadOne = (id: string): void => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: 'uploading' as const, error: null } : it)));
    const item = items.find((x) => x.id === id);
    if (!item) return;

    const setProgress = (value: number): void =>
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, progress: value } : it)));
    const setStateError = (msg: string | null): void =>
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, error: msg } : it)));
    const setFileName = (name: string): void =>
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, fileName: name } : it)));
    const setIsFinalizing = (flag: boolean): void =>
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, isFinalizing: flag } : it)));

    const fakeEvent = { target: { files: [item.file] } };
    handleFileUpload(fakeEvent, setProgress, setStateError, setFileName, {
      mediaGroupName,
      setIsFinalizing,
      nativeFields: [
        ...(item.title ? [{ slug: 'title', content: item.title }] : []),
        ...(item.description ? [{ slug: 'description', content: item.description }] : []),
      ],
      onFinalize: (data) => {
        setItems((prev) =>
          prev.map((it) => (it.id === id ? { ...it, finalizeData: data, status: 'done' as const, progress: 100 } : it)),
        );
        setSnack({ open: true, severity: 'success', message: `Uploaded ${item.fileName}` });
      },
    });
  };

  const uploadAll = (): void => {
    items
      .filter((it) => it.status === 'queued')
      .forEach((it) => {
        uploadOne(it.id);
      });
  };

  const clearCompleted = (): void => setItems((prev) => prev.filter((it) => it.status !== 'done'));

  const closeSnack = (): void => setSnack((s) => ({ ...s, open: false }));

  const mediaGroupOptions = useMemo(() => (mgData?.allMediaGroups?.edges || []).map(({ node }) => node), [mgData]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if ((e.key === 'Enter' || e.key === ' ') && mediaGroupName) browse();
  };

  const handleMediaGroupChange = (e: SelectChangeEvent<string>): void => {
    setMediaGroupName(e.target.value);
  };

  return (
    <div>
      <Box display="flex" gap={12} alignItems="center" mb={2} flexWrap="wrap">
        <FormControl
          variant="outlined"
          size="small"
          disabled={mgLoading}
          error={Boolean(mgError)}
          style={{ minWidth: 260 }}
        >
          <InputLabel id="queue-mg-label">Upload to Media Group</InputLabel>
          <Select
            labelId="queue-mg-label"
            id="queue-media-group-select"
            value={mediaGroupName}
            onChange={handleMediaGroupChange}
            label="Upload to Media Group"
          >
            <MenuItem value="">
              <em>Select a media group...</em>
            </MenuItem>
            {mediaGroupOptions.map((mg) => (
              <MenuItem key={mg.id} value={mg.name}>
                {mg.name}
              </MenuItem>
            ))}
          </Select>
          {mgError ? <FormHelperText>Failed to load media groups</FormHelperText> : null}
        </FormControl>

        <input ref={inputRef} type="file" style={{ display: 'none' }} multiple onChange={onFileChange} />

        <Button
          variant="text"
          color="primary"
          onClick={uploadAll}
          disabled={!items.some((it) => it.status === 'queued') || !mediaGroupName}
          style={{ fontWeight: 600 }}
        >
          Upload All
        </Button>
        <Button variant="text" onClick={clearCompleted} disabled={!items.some((it) => it.status === 'done')}>
          Clear Completed
        </Button>
      </Box>
      <Paper
        elevation={1}
        className={`dropzone ${isDragging ? 'dragging' : ''}`}
        onDrop={onDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => {
          if (mediaGroupName) browse();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        style={{ padding: 12, marginBottom: 12 }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" p={2}>
          <CloudUploadIcon color="primary" style={{ fontSize: 36, opacity: 0.85 }} />
          <Typography variant="subtitle1" style={{ marginTop: 8 }}>
            Drag & drop files here, or click to browse
          </Typography>
          {!mediaGroupName && (
            <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
              Select a Media Group to enable uploads
            </Typography>
          )}
        </Box>
      </Paper>
      {items.map((it) => (
        <QueuedUploaderItem
          key={it.id}
          item={it}
          onChangeMeta={onChangeMeta}
          onUpload={uploadOne}
          onRemove={onRemove}
        />
      ))}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={closeSnack}>
        <MuiAlert onClose={closeSnack} severity={snack.severity} elevation={6} variant="filled">
          {snack.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default QueuedUploaderManager;
